/**
 * Selective Webinar-Invite Modal
 * ------------------------------
 * Lets the admin search for individual contacts by name (or email) and
 * send the WEBINAR:<slug> invite mail to exactly those people. Useful
 * when only a few specific leads should hear about the next webinar
 * (e.g. someone called you, wants to come, but isn't in the broadcast
 * cohort yet).
 *
 * Backend: POST /api/admin/webinars/{webinar_id}/invite-contacts
 *   { contacts: [{collection, id}, ...] }
 *
 * Idempotent at the mail-history level — contacts who already received
 * the invite get a "skipped" status in the result table.
 */
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Search, X, Send, Loader2, CheckCircle2, AlertCircle, MinusCircle } from "lucide-react";

const KIND_COLORS = {
  Lead: "bg-sky-100 text-sky-800 border-sky-200",
  Kontakt: "bg-emerald-100 text-emerald-800 border-emerald-200",
  Kunde: "bg-violet-100 text-violet-800 border-violet-200",
  Partner: "bg-amber-100 text-amber-800 border-amber-200",
};

const STATUS_BADGE = {
  sent: { label: "Gesendet", cls: "bg-emerald-100 text-emerald-700", Icon: CheckCircle2 },
  skipped: { label: "Übersprungen", cls: "bg-amber-100 text-amber-700", Icon: MinusCircle },
  failed: { label: "Fehler", cls: "bg-rose-100 text-rose-700", Icon: AlertCircle },
  not_found: { label: "Nicht gefunden", cls: "bg-slate-100 text-slate-600", Icon: AlertCircle },
};

export default function WebinarTargetedInviteModal({ webinar, apiBase, token, onClose }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState([]);
  const [sending, setSending] = useState(false);
  const [report, setReport] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  // Debounced typeahead search — 250 ms after the last keystroke.
  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) { setResults([]); return; }
    let cancelled = false;
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const r = await axios.get(`${apiBase}/admin/contacts/search`, {
          params: { q, limit: 20 },
          headers: { "X-Admin-Token": token },
        });
        if (!cancelled) setResults(r.data?.results || []);
      } catch (err) {
        if (!cancelled) {
          const msg = err?.response?.data?.detail || "Suche fehlgeschlagen.";
          toast.error(typeof msg === "string" ? msg : "Suche fehlgeschlagen.");
        }
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [query, apiBase, token]);

  const toggleSelect = (contact) => {
    setSelected((prev) => {
      const exists = prev.find((p) => p.id === contact.id && p.collection === contact.collection);
      if (exists) {
        return prev.filter((p) => !(p.id === contact.id && p.collection === contact.collection));
      }
      return [...prev, contact];
    });
  };

  const isSelected = (c) =>
    selected.some((p) => p.id === c.id && p.collection === c.collection);

  const send = async () => {
    if (selected.length === 0) return;
    if (!window.confirm(
      `Einladung zum Webinar "${webinar.title}" an ${selected.length} ` +
      `Kontakt${selected.length === 1 ? "" : "e"} senden?\n\n` +
      `Die Einladung geht an alle ausgewählten Kontakte raus — auch wenn sie bereits eingeladen wurden, opt-out gesetzt haben oder schon mal an einem Webinar teilgenommen haben.`
    )) return;
    setSending(true);
    setReport(null);
    try {
      const payload = {
        contacts: selected.map((s) => ({ collection: s.collection, id: s.id })),
      };
      const r = await axios.post(
        `${apiBase}/admin/webinars/${webinar.id}/invite-contacts`,
        payload,
        { headers: { "X-Admin-Token": token } },
      );
      setReport(r.data);
      const sent = r.data?.totals?.sent ?? 0;
      const notFound = r.data?.totals?.not_found ?? 0;
      const failed = r.data?.totals?.failed ?? 0;
      toast.success(`Versand abgeschlossen: ${sent} gesendet${notFound ? `, ${notFound} ohne E-Mail` : ""}${failed ? `, ${failed} Fehler` : ""}.`);
      setSelected([]);
      setQuery("");
      setResults([]);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Versand fehlgeschlagen.";
      toast.error(typeof msg === "string" ? msg : "Versand fehlgeschlagen.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
      data-testid="webinar-targeted-invite-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div className="al-card max-w-2xl w-full max-h-[90vh] flex flex-col overflow-hidden">
        <div className="p-4 sm:p-5 border-b border-slate-200 flex items-start gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-violet-700">
              {webinar.kind === "technical" ? "Selektive Technik-Einladung" : "Selektive Webinar-Einladung"}
            </div>
            <h2 className="text-lg font-bold text-slate-900 mt-1 leading-tight">{webinar.title}</h2>
            <div className="text-xs text-slate-500 mt-0.5">
              {webinar.kind === "technical"
                ? "Suche Kunden/Kontakte nach Namen und schick ihnen die Technik-Einladung gezielt."
                : "Suche Leads/Kunden/Partner nach Namen und schick ihnen die Einladung gezielt."}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="webinar-targeted-close"
            className="ml-auto p-1.5 rounded-md hover:bg-slate-100 text-slate-500"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-4 sm:p-5 space-y-3 overflow-y-auto">
          {/* Search */}
          <div className="relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Name oder E-Mail eingeben (min. 2 Zeichen)…"
              data-testid="webinar-targeted-search"
              className="w-full pl-9 pr-9 py-2.5 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-violet-300"
            />
            {searching ? (
              <Loader2 className="h-4 w-4 absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 animate-spin" />
            ) : null}
          </div>

          {/* Results */}
          {query.trim().length >= 2 && !searching && results.length === 0 ? (
            <div className="text-sm text-slate-400 italic py-2">
              Keine Kontakte für „{query}" gefunden.
            </div>
          ) : null}
          {results.length > 0 && (
            <div className="border border-slate-200 rounded-lg overflow-hidden divide-y divide-slate-100">
              {results.map((c) => (
                <button
                  key={`${c.collection}-${c.id}`}
                  type="button"
                  onClick={() => toggleSelect(c)}
                  data-testid={`webinar-targeted-result-${c.id}`}
                  className={`w-full px-3 py-2 flex items-center gap-3 hover:bg-violet-50 text-left ${
                    isSelected(c) ? "bg-violet-50" : "bg-white"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={isSelected(c)}
                    readOnly
                    className="h-4 w-4 shrink-0 accent-violet-600"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-slate-900 text-sm truncate">{c.name || c.email}</div>
                    <div className="text-xs text-slate-500 truncate">{c.email}</div>
                  </div>
                  <span className={`text-[10px] uppercase tracking-wider font-bold rounded-full px-2 py-0.5 border ${KIND_COLORS[c.kind] || "bg-slate-100 text-slate-700 border-slate-200"}`}>
                    {c.kind}
                  </span>
                </button>
              ))}
            </div>
          )}

          {/* Selection summary */}
          {selected.length > 0 && (
            <div className="bg-violet-50 border border-violet-200 rounded-lg p-3" data-testid="webinar-targeted-selection">
              <div className="flex items-center justify-between mb-2">
                <strong className="text-sm text-violet-900">
                  {selected.length} ausgewählt
                </strong>
                <button
                  type="button"
                  onClick={() => setSelected([])}
                  className="text-xs text-violet-700 hover:text-violet-900 underline"
                >
                  Alle entfernen
                </button>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {selected.map((s) => (
                  <span
                    key={`${s.collection}-${s.id}`}
                    className="inline-flex items-center gap-1 bg-white border border-violet-200 rounded-full pl-2 pr-1 py-0.5 text-xs"
                  >
                    {s.name || s.email}
                    <button
                      type="button"
                      onClick={() => toggleSelect(s)}
                      className="p-0.5 hover:bg-rose-50 rounded-full text-slate-400 hover:text-rose-600"
                      title="Entfernen"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Send report */}
          {report && (
            <div className="space-y-2" data-testid="webinar-targeted-report">
              <div className="text-xs uppercase tracking-widest font-bold text-slate-500">
                Versand-Bericht
              </div>
              <div className="border border-slate-200 rounded-lg divide-y divide-slate-100 max-h-60 overflow-y-auto">
                {report.results.map((r) => {
                  const badge = STATUS_BADGE[r.status] || STATUS_BADGE.failed;
                  const Icon = badge.Icon;
                  return (
                    <div key={`${r.collection}-${r.id}`} className="px-3 py-2 flex items-center gap-3 text-sm">
                      <Icon className={`h-4 w-4 shrink-0 ${r.status === "sent" ? "text-emerald-600" : r.status === "skipped" ? "text-amber-600" : "text-rose-600"}`} />
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-slate-900 text-xs truncate">{r.name || r.email || r.id}</div>
                        {r.reason ? (
                          <div className="text-[11px] text-slate-500 truncate">{r.reason}</div>
                        ) : null}
                      </div>
                      <span className={`text-[10px] uppercase tracking-wider font-bold rounded-full px-2 py-0.5 ${badge.cls}`}>
                        {badge.label}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <div className="p-4 border-t border-slate-200 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            data-testid="webinar-targeted-cancel"
            className="px-3 py-2 rounded-lg text-sm font-semibold text-slate-600 hover:bg-slate-100"
          >
            Schliessen
          </button>
          <button
            type="button"
            onClick={send}
            disabled={selected.length === 0 || sending}
            data-testid="webinar-targeted-send"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-30 disabled:cursor-not-allowed"
          >
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            {sending ? "Sende…" : `An ${selected.length || ""} senden`.trim()}
          </button>
        </div>
      </div>
    </div>
  );
}
